import {useState} from 'react';

export interface UseUploadReturn {
  UploadInput: JSX.Element;
  handleUploadButtonClick(): void;
}

export interface UseUploadProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  register?: (
    ref: HTMLInputElement | null,
    options?: {required?: boolean},
  ) => void;
  maxNumberOfFiles?: number;
  maxFileSize?: number;
  localSelectedFiles: File[];
  setLocalSelectedFiles: (data: File[]) => void;
}

export interface UploadInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  setUploadInputRef(ref: HTMLInputElement | null): void;
  register?: (
    ref: HTMLInputElement | null,
    options?: {required?: boolean},
  ) => void;
}

function UploadInput({
  setUploadInputRef,
  register,
  ...inputProps
}: UploadInputProps): JSX.Element {
  return (
    <input
      data-testid="uploadInput"
      style={{display: 'none'}}
      type="file"
      multiple
      ref={(event) => {
        if (event) {
          setUploadInputRef(event);
          register?.(event, {required: true});
        }
      }}
      {...(inputProps || {})}
    />
  );
}

export const useUpload = ({
  register,
  maxNumberOfFiles,
  maxFileSize,
  localSelectedFiles,
  setLocalSelectedFiles,
}: UseUploadProps): UseUploadReturn => {
  const [uploadInputRef, setUploadInputRef] =
    useState<HTMLInputElement | null>(null);

  function handleButtonClick() {
    if (uploadInputRef) {
      uploadInputRef.click();
    }
  }

  function handleFileInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.files) {
      if ((localSelectedFiles?.length || 0) >= (maxNumberOfFiles || 0)) {
        return;
      }
      const filesArray = Array.from(event.target.files);
      if (maxNumberOfFiles) {
        const files = filesArray.slice(
          0,
          maxNumberOfFiles - localSelectedFiles.length,
        );
        setLocalSelectedFiles([...localSelectedFiles, ...files]);
        return;
      }
      setLocalSelectedFiles([...localSelectedFiles, ...filesArray]);
    }
  }

  return {
    UploadInput: (
      <UploadInput
        register={register}
        setUploadInputRef={setUploadInputRef}
        onChange={handleFileInputChange}
        maxLength={maxFileSize}
      />
    ),
    handleUploadButtonClick: handleButtonClick,
  };
};
